// ABOUTME: Modal dialog for editing family information (name, timezone, daily logging goal)
// ABOUTME: Provides form with validation for updating family settings

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress
} from '@mui/material';
import { validateFamilyName } from '../../../utils/familyValidation';
import { useFamilyTerminology } from '../../../hooks/useFamilyTerminology';
import type { FamilyInfo, EditFamilyInfoForm} from '../types';

interface EditFamilyInfoModalProps {
  open: boolean;
  familyInfo: FamilyInfo | null;
  onClose: () => void;
  onSave: (formData: EditFamilyInfoForm) => Promise<void>;
}

const EditFamilyInfoModal: React.FC<EditFamilyInfoModalProps> = ({
  open,
  familyInfo,
  onClose,
  onSave
}) => {
  const { Term } = useFamilyTerminology();
  const [formData, setFormData] = useState<EditFamilyInfoForm>({
    familyName: '',
    timezone: '',
    dailyLoggingGoalPerChild: null,
    themeName: 'default',
    labelLanguageMode: 'english-lead'
  });
  const [familyNameError, setFamilyNameError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && familyInfo) {
      setFormData({
        familyName: familyInfo.name,
        timezone: familyInfo.timezone || '',
        dailyLoggingGoalPerChild: familyInfo.effectiveDailyLoggingGoalPerChild || null,
        themeName: familyInfo.effectiveThemeName || 'default',
        labelLanguageMode: familyInfo.effectiveLabelLanguageMode || 'english-lead'
      });
      setFamilyNameError('');
    }
  }, [open, familyInfo]);

  const handleSave = async () => {
    const nameError = validateFamilyName(formData.familyName);
    if (nameError) {
      setFamilyNameError(nameError);
      return;
    }

    try {
      setSaving(true);
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Error saving family info:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleFamilyNameChange = (value: string) => {
    setFormData({ ...formData, familyName: value });
    setFamilyNameError(validateFamilyName(value));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit {Term} Information</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label={`${Term} Name`}
          value={formData.familyName}
          onChange={(e) => handleFamilyNameChange(e.target.value)}
          margin="normal"
          error={!!familyNameError}
          helperText={familyNameError}
          required
        />

        <FormControl fullWidth margin="normal">
          <InputLabel data-testid="timezone-selector">Timezone</InputLabel>
          <Select
            value={formData.timezone}
            onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
            label="Timezone"
          >
            {/* Major US Timezones */}
            <MenuItem value="America/New_York">Eastern Time</MenuItem>
            <MenuItem value="America/Chicago">Central Time</MenuItem>
            <MenuItem value="America/Denver">Mountain Time</MenuItem>
            <MenuItem value="America/Los_Angeles">Pacific Time</MenuItem>
            <MenuItem value="America/Anchorage">Alaska Time</MenuItem>
            <MenuItem value="Pacific/Honolulu">Hawaii Time</MenuItem>

            {/* Major European Timezones */}
            <MenuItem value="Europe/London">London (GMT)</MenuItem>
            <MenuItem value="Europe/Paris">Paris/Berlin (CET)</MenuItem>
            <MenuItem value="Europe/Rome">Rome/Madrid (CET)</MenuItem>
            <MenuItem value="Europe/Athens">Athens (EET)</MenuItem>
            <MenuItem value="Europe/Moscow">Moscow (MSK)</MenuItem>

            {/* Major Asian Timezones */}
            <MenuItem value="Asia/Tokyo">Tokyo (JST)</MenuItem>
            <MenuItem value="Asia/Shanghai">Beijing/Shanghai (CST)</MenuItem>
            <MenuItem value="Asia/Kolkata">India (IST)</MenuItem>
            <MenuItem value="Asia/Seoul">Seoul (KST)</MenuItem>
            <MenuItem value="Asia/Singapore">Singapore (SGT)</MenuItem>
            <MenuItem value="Asia/Bangkok">Bangkok (ICT)</MenuItem>

            {/* Major Australian/Oceania Timezones */}
            <MenuItem value="Australia/Sydney">Sydney (AEDT)</MenuItem>
            <MenuItem value="Australia/Melbourne">Melbourne (AEDT)</MenuItem>
            <MenuItem value="Australia/Perth">Perth (AWST)</MenuItem>
            <MenuItem value="Pacific/Auckland">Auckland (NZDT)</MenuItem>

            {/* Major Canadian Timezones */}
            <MenuItem value="America/Toronto">Toronto (EST)</MenuItem>
            <MenuItem value="America/Vancouver">Vancouver (PST)</MenuItem>
            <MenuItem value="America/Edmonton">Edmonton (MST)</MenuItem>

            {/* Other Major Timezones */}
            <MenuItem value="America/Mexico_City">Mexico City (CST)</MenuItem>
            <MenuItem value="America/Sao_Paulo">São Paulo (BRT)</MenuItem>
            <MenuItem value="Africa/Cairo">Cairo (EET)</MenuItem>
            <MenuItem value="Africa/Johannesburg">Johannesburg (SAST)</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth margin="normal">
          <InputLabel id="theme-selector-label">Theme</InputLabel>
          <Select
            labelId="theme-selector-label"
            value={formData.themeName}
            onChange={(e) => setFormData({ ...formData, themeName: e.target.value })}
            label="Theme"
          >
            <MenuItem value="default">Default</MenuItem>
            <MenuItem value="alt">Alt</MenuItem>
            <MenuItem value="nalu-v1">Nalu</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth margin="normal">
          <InputLabel id="label-language-mode-selector-label">Bilingual Labels</InputLabel>
          <Select
            labelId="label-language-mode-selector-label"
            value={formData.labelLanguageMode}
            onChange={(e) => setFormData({ ...formData, labelLanguageMode: e.target.value })}
            label="Bilingual Labels"
          >
            <MenuItem value="english">English only</MenuItem>
            <MenuItem value="english-lead">English, with ʻŌlelo Hawaiʻi</MenuItem>
            <MenuItem value="bilingual">ʻŌlelo Hawaiʻi first</MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Daily Logging Goal Per Child"
          type="number"
          value={formData.dailyLoggingGoalPerChild || ''}
          onChange={(e) => {
            const value = e.target.value === '' ? null : parseInt(e.target.value, 10);
            if (value === null || value >= 0) {
              setFormData({ ...formData, dailyLoggingGoalPerChild: value });
            }
          }}
          margin="normal"
          inputProps={{ min: 0, step: 1 }}
          helperText="Override the site-wide default. Leave empty to use default. Enter 0 or positive number."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || !!familyNameError}
        >
          {saving ? <CircularProgress size={24} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditFamilyInfoModal;
